import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../ChartOptions.css';
import {
  addSortConfiguration,
  deleteSortConfiguration,
  updateChartData,
  updateSortConfiguration,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { FormControl, MenuItem, Select, Tooltip, Button, Popover } from '@mui/material';
import { sortChartData } from '../../ChartAxes/chartDataFunctions/sortChartData';
import { filterShowHideData } from '../ShowHide/filterShowHideData';
import { RootState } from '../../../redux';
import { getNameFromUid } from '../../CommonFunctions/CommonFunctions';
import { checkMinRequiredCards } from '../../ChartAxes/chartDataFunctions/checkMinRequiredCards';
import ManualSort from './ManualSort/ManualSort';
import { SortConfiguration } from '../../../redux/ChartPoperties/ChartControlsInterface';
import ShortUniqueId from 'short-unique-id';
import { palette } from '../../..';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  handleCheckDisableField,
  applyMultiFieldSort,
  getFieldNamesByAxis,
  getFieldUidsByAxis,
  extractFieldsData,
  applyDefaultDateSorting,
} from './reusableFunctions';

const addButtonStyle = {
  backgroundColor: 'rgb(43, 185, 187)',
  height: '25px',
  width: '100%',
  color: 'white',
  textTransform: 'none',
  marginLeft: '4px',
  '&:hover': {
    backgroundColor: 'rgb(43, 185, 187)',
  },
};

const Sort = ({ iconColor }: { iconColor?: string }) => {
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>({});

  const { chartControls, chartProperties, tabTileProps } = useSelector((state: RootState) => ({
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
    tabTileProps: state.tabTileProps,
  }));

  const dispatch = useDispatch();

  const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;

  const data: string | any =
    chartControls.properties[propKey].chartData.length > 0
      ? chartControls.properties[propKey].chartData
      : [];

  const showHide = chartControls.properties[propKey].showHide;
  const sortConfigurations = chartControls.properties[propKey].sortConfigurations || [];

  // Get available fields for sorting (not already in sortConfigurations)
  const availableFields = useMemo(() => {
    const axes = chartProperties.properties[propKey].chartAxes || [];
    const existingFieldIds = sortConfigurations.map((config: SortConfiguration) => config.fieldId);

    return axes
      .filter((axis: any) => axis.name !== 'Filter')
      .flatMap((axis: any) =>
        axis.fields
          .filter((field: any) => !existingFieldIds.includes(field.uId))
          .map((field: any) => ({
            fieldId: field.uId,
            fieldName: field.displayname,
            axisName: axis.name,
            agg: field.timeGrain || field.agg,
          })),
      );
  }, [chartProperties.properties[propKey].chartAxes, sortConfigurations]);

  let chartAxes = chartProperties.properties[propKey].chartAxes;

  const rowFieldNames = useMemo(
    () => getFieldNamesByAxis(chartAxes, 'Row'),
    [chartProperties.properties[propKey].chartAxes],
  );

  const columnFieldNames = useMemo(
    () => getFieldNamesByAxis(chartAxes, 'Column'),
    [chartProperties.properties[propKey].chartAxes],
  );

  const measureFieldUids = useMemo(
    () => getFieldUidsByAxis(chartAxes, 'Measure'),
    [chartProperties.properties[propKey].chartAxes],
  );

  const dimensionFieldNames = useMemo(
    () => getFieldNamesByAxis(chartAxes, 'Dimension'),
    [chartProperties.properties[propKey].chartAxes],
  );

  const splitByFieldNames = useMemo(
    () => getFieldNamesByAxis(chartAxes, 'Split By'),
    [chartProperties.properties[propKey].chartAxes],
  );

  const rowFieldsData = useMemo(
    () => extractFieldsData(data, rowFieldNames),
    [data, rowFieldNames],
  );

  const columnFieldsData = useMemo(
    () => extractFieldsData(data, columnFieldNames),
    [data, columnFieldNames],
  );

  useEffect(() => {
    if (
      checkMinRequiredCards(
        chartProperties.properties[propKey],
        chartProperties.properties[propKey].chartType,
      ) &&
      !sortConfigurations.some((config: SortConfiguration) => config.sortOrder !== '')
    ) {
      if (
        typeof chartControls.properties[propKey].showHide?.numberInput === 'number' &&
        showHide?.numberInput !== ''
      ) {
        const showHide = chartControls.properties[propKey].showHide;
        filterShowHideData(
          showHide.operator,
          dispatch,
          chartControls.properties[propKey].serverData,
          propKey,
          showHide.numberInput,
          chartControls,
          chartProperties,
        );
      } else {
        let sortedData = sortChartData(
          chartControls.properties[propKey].serverData,
          chartProperties.properties[propKey],
          chartControls,
          propKey,
        );

        // Apply default date sorting if no explicit sort is set
        sortedData = applyDefaultDateSorting(
          sortedData,
          chartProperties.properties[propKey].chartAxes,
        );

        dispatch(updateChartData(propKey, sortedData));
      }
    }
    //eslint-disable-next-line
  }, [sortConfigurations]);

  // Remove sort configurations for fields that are disabled due to sorting rules
  useEffect(() => {
    if (sortConfigurations.length > 0) {
      // Creating a temporary config to check if a  field is disable
      let tempConfigs = [...sortConfigurations];
      let hasRemovedConfig = false;

      for (let i = sortConfigurations.length - 1; i >= 0; i--) {
        const config = sortConfigurations[i];
        // Check with the currently updated configs
        const isDisabled = handleCheckDisableField(
          config.fieldId,
          propKey,
          chartProperties,
          tempConfigs,
        );

        if (config.sortOrder !== '' && isDisabled) {
          dispatch(deleteSortConfiguration(propKey, i));
          tempConfigs = tempConfigs.filter((_, idx) => idx !== i);
          hasRemovedConfig = true;
        }
      }

      // If we removed any configurations, trigger immediate data update
      if (hasRemovedConfig) {
        // Apply default sorting or remaining sort configurations
        if (
          typeof chartControls.properties[propKey].showHide?.numberInput === 'number' &&
          showHide?.numberInput !== ''
        ) {
          const showHide = chartControls.properties[propKey].showHide;
          filterShowHideData(
            showHide.operator,
            dispatch,
            chartControls.properties[propKey].serverData,
            propKey,
            showHide.numberInput,
            chartControls,
            chartProperties,
          );
        } else {
          let sortedData = sortChartData(
            chartControls.properties[propKey].serverData,
            chartProperties.properties[propKey],
            chartControls,
            propKey,
          );
          sortedData = applyDefaultDateSorting(
            sortedData,
            chartProperties.properties[propKey].chartAxes,
          );
          dispatch(updateChartData(propKey, sortedData));
        }
      }
    }
    //eslint-disable-next-line
  }, [
    sortConfigurations,
    chartProperties.properties[propKey].chartAxes,
    chartProperties.properties[propKey].chartType,
  ]);

  // reset sortConfiguration on axis change(agg change or rename etc.)
  useEffect(() => {
    if (sortConfigurations.length > 0) {
      for (let i = 0; i < sortConfigurations.length; i++) {
        const config = sortConfigurations[i];
        const currentFieldName = getNameFromUid(
          chartProperties.properties[propKey].chartAxes,
          config.fieldId,
        );
        if (currentFieldName && currentFieldName !== config.fieldName) {
          // Find the field to get its current aggregator
          let previousAgg = config.agg;
          let currentAgg = previousAgg;
          for (const axis of chartAxes) {
            const field = axis.fields?.find((field: any) => field.uId === config.fieldId);
            if (field) {
              currentAgg = field.timeGrain || field.agg;
              break;
            }
          }

          let updatedConfig;
          // If manual sort is configured, reset it only when aggregator has changed
          if (
            config.sortOrder === 'Manual Sort' &&
            config.manualSortOrderObject &&
            previousAgg &&
            previousAgg !== currentAgg
          ) {
            updatedConfig = {
              ...config,
              fieldName: currentFieldName,
              sortOrder: '',
              manualSortOrderObject: undefined,
              agg: currentAgg,
            };
          } else {
            updatedConfig = {
              ...config,
              fieldName: currentFieldName,
              agg: currentAgg,
            };
          }

          dispatch(updateSortConfiguration(propKey, i, updatedConfig));
        }
      }
    }
    //eslint-disable-next-line
  }, [chartProperties.properties[propKey].chartAxes]);

  useEffect(() => {
    if (
      checkMinRequiredCards(
        chartProperties.properties[propKey],
        chartProperties.properties[propKey].chartType,
      )
    ) {
      applyMultiFieldSort({
        data,
        sortConfigurations,
        chartControls,
        chartProperties,
        propKey,
        showHide,
        dispatch,
        rowFieldsData,
        columnFieldsData,
        rowFieldNames,
        columnFieldNames,
        dimensionFieldNames,
        splitByFieldNames,
        measureFieldUids,
      });
    }
    //eslint-disable-next-line
  }, [sortConfigurations, chartProperties.properties[propKey].chartAxes]);

  // Check if a field is a measure
  const isFieldMeasure = (fieldId: string) => {
    const measureAxis = chartProperties.properties[propKey].chartAxes?.find(
      (axis: any) => axis.name === 'Measure',
    );
    return measureAxis?.fields.some((field: any) => field.uId === fieldId);
  };

  const handleAddSortConfiguration = (field: any) => {
    const uId = new ShortUniqueId({ length: 8 });
    const newConfig: SortConfiguration = {
      id: uId(),
      fieldId: field.fieldId,
      fieldName: field.fieldName,
      sortOrder: '',
      agg: field.agg,
    };

    dispatch(addSortConfiguration(propKey, newConfig));
    setOpenMenu(false);
  };

  const toggleExpanded = (id: string) => {
    setExpandedStates((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSortOrderChange = (index: number, newSortOrder: string) => {
    const config = sortConfigurations[index];
    const updatedConfig = {
      ...config,
      sortOrder: newSortOrder,
    };
    dispatch(updateSortConfiguration(propKey, index, updatedConfig));
  };

  // Check if a field should be disabled based on sorting rules
  const isFieldDisabled = (fieldId: string) => {
    const isDisabled = handleCheckDisableField(
      fieldId,
      propKey,
      chartProperties,
      sortConfigurations,
    );
    return isDisabled;
  };

  return (
    <React.Fragment>
      {checkMinRequiredCards(
        chartProperties.properties[propKey],
        chartProperties.properties[propKey].chartType,
      ) ? (
        /* Enable Sort Component */
        <div>
          <div
            className="sort"
            style={{
              marginBottom: '8px',
              marginTop: '20px',
              justifyContent: 'space-between',
              paddingRight: '1rem',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            SORTBY
          </div>

          {/* Display existing sort configurations */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              paddingRight: '0.5rem',
              padding: '0.2rem 0.4rem 0rem 0.6rem',
            }}
          >
            {sortConfigurations.map((config: SortConfiguration, index: number) => {
              const isExpanded = expandedStates[config.id] !== false; // Default to expanded

              return (
                <div
                  key={config.id}
                  style={{
                    width: '100%',
                    borderBottom: '2px solid rgb(224,224,224)',
                    marginBottom: '0.5rem',
                    paddingBottom: '0.6rem',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '13px',
                        color: iconColor ? iconColor : palette.primary.contrastText,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 'calc(100% - 50px)',
                      }}
                    >
                      {getNameFromUid(
                        chartProperties.properties[propKey].chartAxes,
                        config.fieldId,
                      )}
                    </span>

                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Tooltip
                        title="Delete"
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: '12px',
                            },
                          },
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-eraser-icon lucide-eraser"
                          onClick={() => dispatch(deleteSortConfiguration(propKey, index))}
                          style={{ cursor: 'pointer', color: iconColor ? iconColor : 'inherit' }}
                        >
                          <path d="M21 21H8a2 2 0 0 1-1.42-.587l-3.994-3.999a2 2 0 0 1 0-2.828l10-10a2 2 0 0 1 2.829 0l5.999 6a2 2 0 0 1 0 2.828L12.834 21" />
                          <path d="m5.082 11.09 8.828 8.828" />
                        </svg>
                      </Tooltip>

                      {isExpanded ? (
                        <ExpandMoreIcon
                          sx={{
                            fontSize: '16px',
                            cursor: 'pointer',
                            color: iconColor ? iconColor : 'inherit',
                          }}
                          onClick={() => toggleExpanded(config.id)}
                        />
                      ) : (
                        <ChevronRightIcon
                          sx={{
                            fontSize: '16px',
                            cursor: 'pointer',
                            color: iconColor ? iconColor : 'inherit',
                          }}
                          onClick={() => toggleExpanded(config.id)}
                        />
                      )}
                    </span>
                  </div>

                  {isExpanded && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <FormControl fullWidth sx={{ width: '100%' }}>
                        <Select
                          sx={{
                            width: '99%',
                            height: '26px',
                            fontSize: '13px',
                            paddingRight: '8px',
                            textAlign: 'left',
                            '& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input': {
                              padding: '10.5px 14px',
                            },
                            '&.MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: '1px solid rgb(211, 211, 211)',
                              },
                              '&:hover fieldset': {
                                border: '1px solid #2bb9bb',
                              },
                              '&.Mui-focused fieldset': {
                                border: '1px solid #2bb9bb',
                              },
                              '&.Mui-focused svg': {
                                color: iconColor ? iconColor : '#2bb9bb',
                              },
                              '& svg': {
                                color: iconColor ? iconColor : '#2bb9bb',
                              },
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                '& .MuiMenuItem-root.Mui-selected': {
                                  backgroundColor: 'rgba(43, 185, 187, 0.1) !important',
                                },
                                '& .MuiMenuItem-root.Mui-selected:hover': {
                                  backgroundColor: 'rgba(43, 185, 187, 0.2) !important',
                                },
                              },
                            },
                          }}
                          disabled={isFieldDisabled(config.fieldId)}
                          onChange={(event) => handleSortOrderChange(index, event.target.value)}
                          value={config.sortOrder || ''}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return (
                                <span style={{ color: iconColor ? iconColor : '#999' }}>
                                  Select sort type
                                </span>
                              );
                            }
                            return (
                              <span style={{ color: iconColor ? iconColor : 'inherit' }}>
                                {selected}
                              </span>
                            );
                          }}
                        >
                          <MenuItem
                            value="Ascending"
                            sx={{
                              color: 'black',
                              fontSize: '12px',
                              padding: '2px 10px',
                              '&:hover': { backgroundColor: 'rgb(238, 238, 238)' },
                            }}
                          >
                            Ascending
                          </MenuItem>
                          <MenuItem
                            value="Descending"
                            sx={{
                              color: 'black',
                              fontSize: '12px',
                              padding: '2px 10px',
                              '&:hover': { backgroundColor: 'rgb(238, 238, 238)' },
                            }}
                          >
                            Descending
                          </MenuItem>
                          {chartProperties.properties[propKey].chartType !== 'calendar' && (
                            <MenuItem
                              value="Manual Sort"
                              disabled={isFieldMeasure(config.fieldId)}
                              sx={{
                                color: 'black',
                                fontSize: '12px',
                                padding: '2px 10px',
                                '&:hover': { backgroundColor: 'rgb(238, 238, 238)' },
                              }}
                            >
                              Manual Sort
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>

                      {config.sortOrder === 'Manual Sort' && (
                        <ManualSort propKey={propKey} sortConfigIndex={index} color={iconColor} />
                      )}
                    </div>
                  )}
                  {chartProperties.properties[propKey].chartType === 'calendar' && (
                    <p
                      style={{
                        marginBottom: '2px',
                        fontWeight: '600',
                        textAlign: 'left',
                        marginTop: '3px',
                        color: iconColor ? iconColor : 'inherit',
                      }}
                      className="noteStyle"
                    >
                      *Years are sorted based on sort order*
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          {/* Add button */}
          <div
            style={{
              paddingInline: '6px 11px',
              marginBottom: '1rem',
              marginTop: sortConfigurations.length > 0 ? '0.6rem' : '',
            }}
          >
            <Button
              sx={addButtonStyle}
              disabled={availableFields.length === 0}
              onClick={(e: any) => {
                setAnchorEl(e.currentTarget);
                setOpenMenu(true);
              }}
            >
              Add
            </Button>
          </div>

          {/* Popover for selecting fields */}
          <Popover
            open={openMenu}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
            onClose={() => setOpenMenu(false)}
          >
            {availableFields.map((field: any, index: number) => (
              <div
                key={field.fieldId}
                className="menuItemStyle"
                style={{
                  borderBottom:
                    index === availableFields.length - 1 ? 'none' : '1px solid rgba(224,224,224,1)',
                  cursor: isFieldDisabled(field.fieldId) ? 'not-allowed' : 'pointer',
                  padding: '8px 12px',
                  fontSize: '13px',
                  minWidth: '150px',
                  opacity: isFieldDisabled(field.fieldId) ? 0.5 : 1,
                  color: isFieldDisabled(field.fieldId) ? '#999' : 'inherit',
                }}
                onClick={() => !isFieldDisabled(field.fieldId) && handleAddSortConfiguration(field)}
              >
                {field.fieldName}
              </div>
            ))}
          </Popover>
        </div>
      ) : (
        /* Disabled Sort Component */
        <div>
          <div
            className="sortDisable"
            style={{
              marginBottom: '8px',
              marginTop: '20px',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            SORTBY
          </div>

          <div
            style={{
              paddingInline: '6px 11px',
              marginBottom: '1rem',
              marginTop: sortConfigurations.length > 0 ? '0.6rem' : '',
            }}
          >
            <Button sx={addButtonStyle} disabled={true}>
              Add
            </Button>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default Sort;
